import React from "react";
import type { WeatherData } from "../../types";
import roddoFish from "../../assets/roddo-fish.png";
import WaterIntelDock from "../waterIntel/WaterIntelDock";
import RoddoRadioMapWidget from "../RoddoRadioMapWidget";
import RoddoRadioNowPlayingBanner from "../RoddoRadioNowPlayingBanner";

type LatLng = { lat: number; lng: number };

type Props = {
  isDarkMode: boolean;
  isActive: boolean;
  bottomHudCss: string;
  actionButtonStyle: string;
  suppressWaterIntelLegend: boolean;
  getMap: () => any;
  weatherData?: WeatherData | null;
  savedSpotsAnchorRef: React.RefObject<HTMLButtonElement | null>;
  isSavedSpotsOpen: boolean;
  onWaterIntelLogCatch: (target: LatLng) => void;
  onWaterIntelSearchFishIQ: (target: LatLng) => void;
  onOpenFishIQ: () => void;
  onToggleSavedSpots: () => void;
  mapActionAnchor: LatLng | null;
  isMapActionCardOpen: boolean;
  mapActionWaterRead: any | null;
  mapActionWaterLoading: boolean;
  mapActionHasFishIQ: boolean;
  mapActionFishIQCount: number;
  isSearchingSavedArea: boolean;
  onCloseMapAction: () => void;
  onMapActionFishIQ: (target: LatLng, hasExistingRead: boolean) => void;
  onMapActionLog: (target: LatLng) => void;
  onMapActionSave: (target: LatLng) => void;
};

export default function BigBlueWaterTools({
  isDarkMode,
  isActive,
  bottomHudCss,
  actionButtonStyle,
  suppressWaterIntelLegend,
  getMap,
  weatherData,
  savedSpotsAnchorRef,
  isSavedSpotsOpen,
  onWaterIntelLogCatch,
  onWaterIntelSearchFishIQ,
  onOpenFishIQ,
  onToggleSavedSpots,
  mapActionAnchor,
  isMapActionCardOpen,
  mapActionWaterRead,
  mapActionWaterLoading,
  mapActionHasFishIQ,
  mapActionFishIQCount,
  isSearchingSavedArea,
  onCloseMapAction,
  onMapActionFishIQ,
  onMapActionLog,
  onMapActionSave,
}: Props) {
  const current = mapActionWaterRead?.currents;
  const bathymetry = mapActionWaterRead?.bathymetry;
  const depth = Number(bathymetry?.depthMeters);
  const relief = Number(bathymetry?.localReliefMeters);
  const currentSpeed = Number(current?.speedKnots);
  const currentDirection = String(current?.directionLabel || "").trim();
  const structures = Array.isArray(bathymetry?.structureTypes)
    ? bathymetry.structureTypes
        .map((value: any) => String(value || "").replace(/[_-]+/g, " ").trim())
        .filter(Boolean)
    : [];
  const structureRaw = structures[0] || String(
    bathymetry?.structureType || bathymetry?.slopeClass || "",
  ).replace(/[_-]+/g, " ").trim();
  const structure = /^(unknown|none|n\/?a)$/i.test(structureRaw) ? "" : structureRaw;
  const hasDepth = bathymetry?.available === true && Number.isFinite(depth);
  const hasCurrent = current?.available === true && Number.isFinite(currentSpeed);
  const statValueStyle = (available: boolean): React.CSSProperties => ({
    color: available
      ? isDarkMode ? "#F2F6FC" : "#3D342D"
      : isDarkMode ? "rgba(174,184,201,0.58)" : "rgba(117,106,96,0.58)",
  });

  return (
    <>
      <div
        className="absolute left-[68px] right-[68px] sm:left-[84px] sm:right-[84px] min-[1024px]:left-[72px] min-[1024px]:right-auto min-[1024px]:w-[304px] pointer-events-none z-[1002]"
        style={{ bottom: bottomHudCss }}
      >
        <RoddoRadioNowPlayingBanner isDarkMode={isDarkMode} placement="bottom" />
      </div>

      <div
        className="absolute left-2 sm:left-4 pointer-events-auto flex flex-col space-y-2 z-[1000]"
        style={{ bottom: bottomHudCss }}
      >
        <WaterIntelDock
          isDarkMode={isDarkMode}
          isActive={isActive}
          suppressLegend={suppressWaterIntelLegend}
          getMap={getMap}
          weatherData={weatherData as any}
          onLogCatch={onWaterIntelLogCatch}
          onSearchFishIQ={onWaterIntelSearchFishIQ}
        />

        <button
          type="button"
          data-roddo-tour="map-fishiq"
          data-roddo-quick-anchor="fishiq"
          className={actionButtonStyle}
          onClick={onOpenFishIQ}
          aria-label="Water Read"
          title="Water Read"
        >
          <img
            src={roddoFish}
            alt=""
            aria-hidden="true"
            draggable={false}
            className="h-[30px] w-[34px] object-contain transition-transform duration-200 ease-out group-hover:scale-[1.06] group-active:scale-[0.94]"
            style={{
              filter: isDarkMode
                ? "drop-shadow(0 4px 9px rgba(110,99,255,0.28))"
                : "drop-shadow(0 4px 8px rgba(37,91,255,0.16))",
            }}
          />
        </button>

        <button
          ref={savedSpotsAnchorRef}
          type="button"
          className={`${actionButtonStyle} ${isSavedSpotsOpen ? "ring-2 ring-[#F2B84B]/60 dark:ring-[#F2B84B]/42" : ""}`}
          onClick={onToggleSavedSpots}
          aria-label="Saved spots"
          title="Saved spots"
        >
          <span
            className="text-[24px] leading-none transition-transform duration-200 ease-out group-hover:scale-[1.10] group-active:scale-[0.92]"
            style={{
              color: isDarkMode ? "#FFE082" : "#D89B12",
              filter: isDarkMode
                ? "drop-shadow(0 0 10px rgba(255,224,130,0.38))"
                : "drop-shadow(0 0 7px rgba(216,155,18,0.24))",
              transition: "color 180ms ease, filter 180ms ease, transform 180ms ease",
            }}
          >
            ★
          </span>
        </button>

        <RoddoRadioMapWidget isDarkMode={isDarkMode} />
      </div>

      {mapActionAnchor && isMapActionCardOpen ? (
        <div
          className="pointer-events-auto absolute left-1/2 z-[6400] w-[min(356px,calc(100%-24px))] -translate-x-1/2"
          style={{
            bottom: "calc(var(--roddo-map-bottom-clearance, var(--roddo-nav-h, 84px)) + env(safe-area-inset-bottom) + 14px)",
          }}
          onClick={(event) => event.stopPropagation()}
        >
          <div
            className="overflow-hidden rounded-[22px] border shadow-2xl backdrop-blur-xl"
            style={{
              background: isDarkMode ? "rgba(10,17,32,0.975)" : "rgba(255,250,243,0.98)",
              borderColor: isDarkMode ? "rgba(132,151,190,0.16)" : "rgba(91,67,49,0.12)",
              color: isDarkMode ? "#F2F6FC" : "#3D342D",
            }}
          >
            <div className="flex items-start justify-between gap-3 px-3.5 pb-2.5 pt-3.5">
              <div className="min-w-0">
                <div
                  className="text-[8.5px] font-black uppercase tracking-[0.18em]"
                  style={{ color: isDarkMode ? "#66D9E8" : "#24779B" }}
                >
                  Selected water
                </div>
                <div className="mt-1 text-[15px] font-black">Point of interest</div>
                <div
                  className="mt-1 text-[9px] font-extrabold tabular-nums"
                  style={{ color: isDarkMode ? "#AEB8C9" : "#756A60" }}
                >
                  {Math.abs(mapActionAnchor.lat).toFixed(5)}° {mapActionAnchor.lat >= 0 ? "N" : "S"} · {Math.abs(mapActionAnchor.lng).toFixed(5)}° {mapActionAnchor.lng >= 0 ? "E" : "W"}
                </div>
              </div>
              <button
                type="button"
                onClick={onCloseMapAction}
                className="grid h-8 w-8 shrink-0 place-items-center rounded-full border text-[15px] font-black active:scale-95"
                style={{
                  background: isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(255,255,255,0.72)",
                  borderColor: isDarkMode ? "rgba(255,255,255,0.10)" : "rgba(91,67,49,0.10)",
                  color: isDarkMode ? "#DDE5F0" : "#665A4F",
                }}
                aria-label="Clear selected point"
                title="Clear selected point"
              >
                ×
              </button>
            </div>

            <div className="grid grid-cols-3 gap-2 px-3.5 pb-3">
              <div className="flex min-h-[58px] flex-col justify-center rounded-[13px] border px-2.5 py-2.5" style={{ borderColor: isDarkMode ? "rgba(102,217,232,0.13)" : "rgba(86,174,220,0.18)", background: isDarkMode ? "rgba(255,255,255,0.025)" : "rgba(255,255,255,0.46)" }}>
                <div className="text-[7.5px] font-black uppercase tracking-[0.12em] opacity-55">Depth</div>
                <div className="mt-1 truncate text-[11px] font-black" style={statValueStyle(hasDepth)}>{hasDepth ? `${Math.round(depth)} m` : mapActionWaterLoading ? "Reading…" : "—"}</div>
              </div>
              <div className="flex min-h-[58px] flex-col justify-center rounded-[13px] border px-2.5 py-2.5" style={{ borderColor: isDarkMode ? "rgba(102,217,232,0.13)" : "rgba(86,174,220,0.18)", background: isDarkMode ? "rgba(255,255,255,0.025)" : "rgba(255,255,255,0.46)" }}>
                <div className="text-[7.5px] font-black uppercase tracking-[0.12em] opacity-55">Current</div>
                <div className="mt-1 truncate text-[11px] font-black" style={statValueStyle(hasCurrent)}>{hasCurrent ? `${currentSpeed < 1 ? currentSpeed.toFixed(2) : currentSpeed.toFixed(1)} kt${currentDirection ? ` ${currentDirection}` : ""}` : mapActionWaterLoading ? "Reading…" : "—"}</div>
              </div>
              <div className="flex min-h-[58px] flex-col justify-center rounded-[13px] border px-2.5 py-2.5" style={{ borderColor: isDarkMode ? "rgba(102,217,232,0.13)" : "rgba(86,174,220,0.18)", background: isDarkMode ? "rgba(255,255,255,0.025)" : "rgba(255,255,255,0.46)" }}>
                <div className="text-[7.5px] font-black uppercase tracking-[0.12em] opacity-55">Structure</div>
                <div className="mt-1 truncate text-[11px] font-black" style={statValueStyle(Boolean(structure) || (Number.isFinite(relief) && relief > 0))}>{structure || (Number.isFinite(relief) && relief > 0 ? `${Math.round(relief)} m relief` : mapActionWaterLoading ? "Reading…" : "—")}</div>
              </div>
            </div>

            <div
              className="grid grid-cols-[1.18fr_0.91fr_0.91fr] gap-2 border-t px-3.5 py-3"
              style={{ borderColor: isDarkMode ? "rgba(132,151,190,0.10)" : "rgba(91,67,49,0.08)" }}
            >
              <button
                type="button"
                disabled={isSearchingSavedArea}
                onClick={() => onMapActionFishIQ(mapActionAnchor, mapActionHasFishIQ)}
                className="flex h-[44px] items-center justify-center gap-2 whitespace-nowrap rounded-[13px] border px-3 text-[12px] font-black uppercase tracking-[0.055em] active:scale-[0.985] disabled:cursor-wait disabled:opacity-70"
                style={{
                  background: isDarkMode ? "rgba(102,217,232,0.12)" : "rgba(234,247,255,0.96)",
                  borderColor: isDarkMode ? "rgba(102,217,232,0.22)" : "rgba(86,174,220,0.28)",
                  color: isDarkMode ? "#DDF6FF" : "#22638A",
                }}
                title={mapActionHasFishIQ ? "Open Water Read for this point" : "Search water read · costs 1 Search Lure"}
              >
                {isSearchingSavedArea ? (
                  <span aria-hidden="true">…</span>
                ) : mapActionHasFishIQ ? (
                  <span>Water Read{mapActionFishIQCount ? ` ${mapActionFishIQCount}` : ""}</span>
                ) : (
                  <>
                    <span>Search</span>
                    <span aria-hidden="true" className="text-[13px] leading-none">🪝</span>
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={() => onMapActionLog(mapActionAnchor)}
                className="h-[44px] rounded-[13px] border px-2.5 text-[12px] font-black uppercase tracking-[0.055em] active:scale-[0.985]"
                style={{
                  background: isDarkMode ? "rgba(102,214,169,0.12)" : "rgba(234,255,245,0.96)",
                  borderColor: isDarkMode ? "rgba(102,214,169,0.18)" : "rgba(172,235,204,1)",
                  color: isDarkMode ? "#DFFFF1" : "#256A54",
                }}
              >
                Log
              </button>
              <button
                type="button"
                onClick={() => onMapActionSave(mapActionAnchor)}
                className="h-[44px] rounded-[13px] border px-2.5 text-[12px] font-black uppercase tracking-[0.055em] active:scale-[0.985]"
                style={{
                  background: isDarkMode ? "rgba(242,184,75,0.12)" : "rgba(255,248,221,0.94)",
                  borderColor: isDarkMode ? "rgba(242,184,75,0.20)" : "rgba(160,120,40,0.20)",
                  color: isDarkMode ? "#F2D28A" : "#8A6200",
                }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
